/**
 * Sign-in links: the personal link in a request or reminder email.
 *
 * Shared by the API (mint, exchange, email) and the portal (read, fallback),
 * so both agree on the address shape, the lifetime and the failure reasons.
 */

/** A link works one time, for this long after the email is delivered. */
export const portalSigninLinkHours = 72

/** The query parameter that carries the raw token. The portal strips it from
    the address bar before it asks the API to exchange it. */
export const portalSigninLinkParam = "s"

/** The fragment that opens one request on the client's page. */
export const portalRequestAnchor = (requestId: string): string =>
  `request-${requestId}`

/** Expiry counts from delivery, not from minting: a reminder scheduled four
    days ahead still gets its full 72 hours once it lands. */
export const portalSigninLinkExpiresAt = (deliveredAt: Date): Date =>
  new Date(deliveredAt.getTime() + portalSigninLinkHours * 60 * 60 * 1000)

/** The client link with a sign-in token and, when known, the request to
    open. */
export const withPortalSigninLink = (
  portalUrl: string,
  token: string,
  requestId: string | null
): string => {
  const url = new URL(portalUrl)
  url.searchParams.set(portalSigninLinkParam, token)
  if (requestId) url.hash = portalRequestAnchor(requestId)
  return url.toString()
}

export type PortalSigninLinkState = "usable" | "used" | "expired" | "revoked"

/** Revoked wins over everything, then a use, then the clock. A used link that
    has also expired reads as used, so the portal offers a code, not a link. */
export const portalSigninLinkState = (
  link: Readonly<{
    consumedAt: Date | null
    expiresAt: Date
    revokedAt: Date | null
  }>,
  now: Date
): PortalSigninLinkState => {
  if (link.revokedAt) return "revoked"
  if (link.consumedAt) return "used"
  return link.expiresAt <= now ? "expired" : "usable"
}

export const portalSigninLinkFailureReasons = [
  "expired",
  "used",
  "invalid",
] as const

export type PortalSigninLinkFailureReason =
  (typeof portalSigninLinkFailureReasons)[number]

/** An address shown to whoever holds a used or expired link: enough for the
    contact to recognise, too little to copy. "jane@acme.com" → "j•••@acme.com". */
export const maskPortalEmail = (email: string): string => {
  const trimmed = email.trim()
  const at = trimmed.lastIndexOf("@")
  if (at <= 0) return "•••"
  return `${trimmed.charAt(0)}•••${trimmed.slice(at)}`
}
